/**
 * Stammdaten: die Lagerorte (KONZEPT.md §5).
 *
 * Der Baum aus Orten und ihren Unterebenen -- nur Lagerorte, keine
 * Packstuecke (ADR 0028). Packstuecke legt das Lager mit „+ Packstück“ an.
 * Angelegt wird dort, wo etwas hingehoert: „+" an einem Knoten legt darunter
 * eine Unterebene an (Regal, Fach, Raum), oben entsteht ein Ort.
 *
 * Lagerorte werden nie geloescht, nur stillgelegt: Buchungen zeigen auf sie.
 *
 * Nur fuer Administratoren und nur mit Netz (ADR 0006).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import type { Lagerort, LagerortEbene, LagerortKnoten } from "@thw/contracts";
import { anfrage, sitzungLesen } from "../../api/klient.js";
import { useOnline } from "../../api/online.js";
import { abgleichen } from "../../lokal/abgleich.js";
import {
  Eingabe,
  Karte,
  Knopf,
  Ladeanzeige,
  NurMitNetz,
  Rueckmeldung,
  Schalter,
} from "../../ui/bausteine.jsx";

const EBENE_TEXT: Record<LagerortEbene, { name: string; beispiel: string }> = {
  ort: { name: "Ort", beispiel: "Halle, LKW, Werkstatt, Anhänger" },
  packstueck: { name: "Packstück", beispiel: "Gitterbox, Kiste, Rollcontainer" },
  unterebene: { name: "Unterebene", beispiel: "Regal, Fach, Raum" },
};

/** Welche Ebene ein neuer Lagerort unter diesem Eltern bekommt. */
function ebeneUnter(eltern: LagerortKnoten | null): LagerortEbene {
  if (eltern === null) return "ort";
  // Packstuecke entstehen nicht hier, sondern ueber „+ Packstueck“ (ADR 0028).
  return "unterebene";
}

/** Kennungen: Grossbuchstaben, Ziffern, Bindestrich -- so steht es auf dem Etikett. */
const kennungSaeubern = (roh: string): string =>
  roh
    .toUpperCase()
    .replaceAll("Ä", "AE")
    .replaceAll("Ö", "OE")
    .replaceAll("Ü", "UE")
    .replaceAll("ß", "SS")
    .replace(/[\s_]+/g, "-")
    .replace(/[^A-Z0-9-]/g, "")
    .slice(0, 16);

const BAUM_SCHLUESSEL = ["verwaltung-lagerorte"] as const;

type Meldung = { art: "gut" | "warnung"; text: string; lagerortId?: string } | null;

/** Flacht den Baum ab -- fuer die Auswahl beim Verschieben. */
function flach(
  knoten: readonly LagerortKnoten[],
  tiefe = 0,
): { k: LagerortKnoten; tiefe: number }[] {
  return knoten.flatMap((k) => [{ k, tiefe }, ...flach(k.kinder, tiefe + 1)]);
}

/** Der Baum ohne Packstuecke und ohne das, was darin liegt (ADR 0028). */
function nurOrte(knoten: readonly LagerortKnoten[]): LagerortKnoten[] {
  return knoten
    .filter((k) => k.ebene !== "packstueck")
    .map((k) => ({ ...k, kinder: nurOrte(k.kinder) }));
}

/** Alle Kennungen im Teilbaum -- dorthin darf ein Knoten nicht verschoben werden. */
function teilbaum(k: LagerortKnoten): Set<string> {
  return new Set([k.id, ...k.kinder.flatMap((kind) => [...teilbaum(kind)])]);
}

export function LagerortVerwaltung() {
  const online = useOnline();
  const [meldung, setMeldung] = useState<Meldung>(null);
  // `undefined` = nichts offen, `null` = neuer Ort oben im Baum.
  const [neuUnter, setNeuUnter] = useState<LagerortKnoten | null | undefined>(undefined);
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);
  const [stillgelegteZeigen, setStillgelegteZeigen] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: BAUM_SCHLUESSEL,
    queryFn: () => anfrage<{ baum: LagerortKnoten[] }>("GET", "/api/core/lagerorte"),
  });

  if (sitzungLesen()?.benutzer.rolle !== "administrator") {
    return <Rueckmeldung art="warnung" kopf="Nur für Administratoren" />;
  }
  if (isPending) return <Ladeanzeige was="Der Lagerortbaum" />;

  // Nur Orte und ihre Unterebenen (ADR 0028) -- Packstuecke pflegt das Lager.
  const baum = nurOrte(data?.baum ?? []);
  const alle = flach(baum);

  const sichtbar = (k: LagerortKnoten): boolean => stillgelegteZeigen || k.aktiv;

  const zeile = (k: LagerortKnoten, tiefe: number) => (
    <li key={k.id} className={sichtbar(k) ? "" : "hidden"}>
      <div
        className={`rounded-xl bg-white dark:bg-neutral-800 ${k.aktiv ? "" : "opacity-60"}`}
        style={{ marginLeft: `${String(tiefe * 14)}px` }}
      >
        <div className="flex min-h-14 items-center gap-2 px-3 py-2">
          <button
            type="button"
            className="flex flex-1 flex-col items-start text-left"
            aria-expanded={bearbeitet === k.id}
            onClick={() => {
              setBearbeitet(bearbeitet === k.id ? null : k.id);
              setNeuUnter(undefined);
            }}
          >
            <span>
              <span className="font-semibold">{k.kennung}</span>
              <span className="text-neutral-600 dark:text-neutral-300"> — {k.bezeichnung}</span>
            </span>
            <span className="flex flex-wrap gap-2 text-xs text-neutral-500 dark:text-neutral-400">
              <span>{EBENE_TEXT[k.ebene].name}</span>
              {k.mobil && <span>mobil</span>}
              {!k.aktiv && <span>stillgelegt</span>}
              <span>{k.anzahl_artikel} Artikel</span>
            </span>
          </button>
          {k.aktiv ? (
            <button
              type="button"
              className="flex size-12 items-center justify-center rounded-xl bg-neutral-100 text-xl font-bold active:bg-neutral-200 disabled:opacity-40 dark:bg-neutral-700"
              aria-label={`Unter ${k.kennung} anlegen`}
              title="Darunter anlegen"
              disabled={!online}
              onClick={() => {
                setNeuUnter(k);
                setBearbeitet(null);
              }}
            >
              +
            </button>
          ) : null}
        </div>

        {bearbeitet === k.id && (
          <LagerortBearbeiten
            knoten={k}
            alle={alle}
            online={online}
            melden={setMeldung}
            fertig={() => {
              setBearbeitet(null);
            }}
          />
        )}
      </div>

      {neuUnter?.id === k.id && (
        <div className="mt-2" style={{ marginLeft: `${String((tiefe + 1) * 14)}px` }}>
          <LagerortAnlegen
            eltern={k}
            online={online}
            melden={setMeldung}
            fertig={() => {
              setNeuUnter(undefined);
            }}
          />
        </div>
      )}

      {k.kinder.length > 0 && (
        <ul className="mt-2 flex flex-col gap-2">
          {k.kinder.map((kind) => zeile(kind, tiefe + 1))}
        </ul>
      )}
    </li>
  );

  const stillgelegte = alle.filter(({ k }) => !k.aktiv).length;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Lagerorte</h1>
      <p className="text-neutral-600 dark:text-neutral-300">
        Mit „+“ legst du unter einem Lagerort an — etwa eine Gitterbox im LKW. Die Ebene ergibt sich
        aus dem Platz im Baum.
      </p>
      {!online && <NurMitNetz />}
      {meldung !== null && (
        <Rueckmeldung
          art={meldung.art}
          kopf={meldung.text}
          zusatz={
            meldung.lagerortId === undefined ? undefined : (
              <Link
                to={`/etiketten?lagerort=${meldung.lagerortId}`}
                className="font-semibold underline"
              >
                Jetzt das Etikett drucken
              </Link>
            )
          }
        />
      )}

      {neuUnter === null ? (
        <LagerortAnlegen
          eltern={null}
          online={online}
          melden={setMeldung}
          fertig={() => {
            setNeuUnter(undefined);
          }}
        />
      ) : (
        <Knopf
          art="haupt"
          disabled={!online}
          onClick={() => {
            setNeuUnter(null);
            setBearbeitet(null);
          }}
        >
          Neuer Ort (oben im Baum)
        </Knopf>
      )}

      {baum.length === 0 ? (
        <p className="text-neutral-500">
          Noch keine Lagerorte. Fang mit einer Halle oder einem LKW an.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">{baum.map((k) => zeile(k, 0))}</ul>
      )}

      {stillgelegte > 0 && (
        <Schalter
          beschriftung={`Stillgelegte zeigen (${String(stillgelegte)})`}
          an={stillgelegteZeigen}
          aendern={setStillgelegteZeigen}
        />
      )}
    </div>
  );
}

// --- Anlegen ------------------------------------------------------------------

function LagerortAnlegen({
  eltern,
  online,
  melden,
  fertig,
}: {
  eltern: LagerortKnoten | null;
  online: boolean;
  melden: (m: Meldung) => void;
  fertig: () => void;
}) {
  const zwischenspeicher = useQueryClient();
  const ebene = ebeneUnter(eltern);
  const [bezeichnung, setBezeichnung] = useState("");
  const [kennung, setKennung] = useState("");
  const [kennungVonHand, setKennungVonHand] = useState(false);
  const [mobil, setMobil] = useState(false);

  const anlegen = useMutation({
    mutationFn: () =>
      anfrage<Lagerort>("POST", "/api/core/lagerorte", {
        ebene,
        kennung,
        bezeichnung: bezeichnung.trim(),
        eltern_id: eltern?.id ?? null,
        mobil,
      }),
    onSuccess: (l) => {
      melden({ art: "gut", text: `✓ ${l.kennung} angelegt`, lagerortId: l.id });
      void zwischenspeicher.invalidateQueries();
      void abgleichen({ stammdaten: true });
      fertig();
    },
    onError: (f) => {
      melden({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
    },
  });

  const gueltig = bezeichnung.trim() !== "" && kennung.length >= 1;

  return (
    <Karte
      titel={
        eltern === null
          ? `Neuer ${EBENE_TEXT[ebene].name}`
          : `Neu in ${eltern.kennung}: ${EBENE_TEXT[ebene].name}`
      }
    >
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Etwa {EBENE_TEXT[ebene].beispiel}.
      </p>
      <Eingabe
        beschriftung="Bezeichnung"
        wert={bezeichnung}
        aendern={(neu) => {
          setBezeichnung(neu);
          if (!kennungVonHand) setKennung(kennungSaeubern(neu));
        }}
      />
      <Eingabe
        beschriftung="Kennung"
        wert={kennung}
        aendern={(neu) => {
          setKennungVonHand(true);
          setKennung(kennungSaeubern(neu));
        }}
        hinweis="Kurz, steht groß auf dem Etikett und im QR-Code — etwa GB-07 oder LKW-1."
      />
      {ebene === "ort" && (
        <Schalter
          beschriftung="Mobil"
          hinweis="Bewegt sich mit – zum Beispiel ein LKW, eine NEA oder ein Anhänger."
          an={mobil}
          aendern={setMobil}
        />
      )}
      <div className="flex gap-2">
        <Knopf
          art="haupt"
          disabled={!online || !gueltig || anlegen.isPending}
          onClick={() => {
            anlegen.mutate();
          }}
        >
          Anlegen
        </Knopf>
        <Knopf art="still" onClick={fertig}>
          Abbrechen
        </Knopf>
      </div>
    </Karte>
  );
}

function LagerortBearbeiten({
  knoten,
  alle,
  online,
  melden,
  fertig,
}: {
  knoten: LagerortKnoten;
  alle: readonly { k: LagerortKnoten; tiefe: number }[];
  online: boolean;
  melden: (m: Meldung) => void;
  fertig: () => void;
}) {
  const zwischenspeicher = useQueryClient();
  const [bezeichnung, setBezeichnung] = useState(knoten.bezeichnung);
  const [kennung, setKennung] = useState(knoten.kennung);
  const [mobil, setMobil] = useState(knoten.mobil);
  const [eltern, setEltern] = useState(knoten.eltern_id ?? "");

  const erfolg = (l: Lagerort, text: string): void => {
    melden({ art: "gut", text, lagerortId: l.id });
    void zwischenspeicher.invalidateQueries();
    // Die lokale Kopie jedes Geraets zieht beim naechsten Abgleich nach,
    // dieses hier sofort.
    void abgleichen({ stammdaten: true });
    fertig();
  };
  const fehler = (f: unknown): void => {
    melden({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
  };

  const aendern = useMutation({
    mutationFn: (aenderung: Record<string, unknown>) =>
      anfrage<Lagerort>("PATCH", `/api/core/lagerorte/${knoten.id}`, aenderung),
    onSuccess: (l) => {
      erfolg(l, `✓ ${l.kennung} gespeichert`);
    },
    onError: fehler,
  });
  const stilllegen = useMutation({
    mutationFn: () => anfrage<Lagerort>("DELETE", `/api/core/lagerorte/${knoten.id}`),
    onSuccess: (l) => {
      erfolg(l, `✓ ${l.kennung} stillgelegt`);
    },
    onError: fehler,
  });

  // Wohin darf verschoben werden? Eine Unterebene in einen Ort oder eine
  // andere Unterebene (ADR 0028), nie in sich selbst.
  const gesperrt = teilbaum(knoten);
  const ziele = alle.filter(({ k }) => k.aktiv && !gesperrt.has(k.id) && k.ebene !== "packstueck");

  const aenderung: Record<string, unknown> = {
    ...(bezeichnung.trim() !== knoten.bezeichnung ? { bezeichnung: bezeichnung.trim() } : {}),
    ...(kennung !== knoten.kennung ? { kennung } : {}),
    ...(mobil !== knoten.mobil ? { mobil } : {}),
    ...(knoten.ebene !== "ort" && eltern !== (knoten.eltern_id ?? "") ? { eltern_id: eltern } : {}),
  };
  const geaendert = Object.keys(aenderung).length > 0;

  return (
    <div className="flex flex-col gap-3 border-t border-neutral-200 p-3 dark:border-neutral-700">
      <Eingabe beschriftung="Bezeichnung" wert={bezeichnung} aendern={setBezeichnung} />
      <Eingabe
        beschriftung="Kennung"
        wert={kennung}
        aendern={(neu) => {
          setKennung(kennungSaeubern(neu));
        }}
        hinweis={
          kennung !== knoten.kennung
            ? "Der QR-Code bleibt gültig, der Text auf dem Etikett zeigt aber die alte Kennung. Etiketten von vor dem festen QR-Code zeigen auf die alte Kennung und müssen neu gedruckt werden."
            : undefined
        }
      />
      {/* Unveränderlich (ADR 0017) -- nur zum Nachsehen, etwa wenn ein Etikett nicht scannt. */}
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        QR-Code: <span className="font-mono font-semibold">{knoten.kurzcode}</span> — bleibt beim
        Umbenennen.
      </p>
      {knoten.ebene === "ort" && (
        <Schalter
          beschriftung="Mobil"
          hinweis="Bewegt sich mit – zum Beispiel ein LKW, eine NEA oder ein Anhänger."
          an={mobil}
          aendern={setMobil}
        />
      )}
      {knoten.ebene !== "ort" && (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold">Liegt in</span>
          <select
            className="min-h-12 rounded-xl border-2 border-neutral-300 bg-white px-3 text-base dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
            value={eltern}
            onChange={(e) => {
              setEltern(e.target.value);
            }}
          >
            {ziele.map(({ k, tiefe }) => (
              <option key={k.id} value={k.id}>
                {"  ".repeat(tiefe)}
                {k.kennung} — {k.bezeichnung}
              </option>
            ))}
          </select>
        </label>
      )}

      <Knopf
        art="haupt"
        disabled={!online || !geaendert || bezeichnung.trim() === "" || kennung === ""}
        onClick={() => {
          aendern.mutate(aenderung);
        }}
      >
        Speichern
      </Knopf>

      <div className="flex flex-wrap gap-2">
        <Link
          to={`/etiketten?lagerort=${knoten.id}`}
          className="flex min-h-12 items-center rounded-xl bg-neutral-200 px-5 font-semibold active:bg-neutral-300 dark:bg-neutral-700"
        >
          Etikett drucken
        </Link>
        {knoten.aktiv ? (
          <Knopf
            art="still"
            disabled={!online}
            onClick={() => {
              stilllegen.mutate();
            }}
          >
            Stilllegen
          </Knopf>
        ) : (
          <Knopf
            disabled={!online}
            onClick={() => {
              aendern.mutate({ aktiv: true });
            }}
          >
            Wieder aktivieren
          </Knopf>
        )}
      </div>
      {knoten.aktiv && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Stillgelegt verschwindet der Lagerort aus den Auswahllisten. Die Historie bleibt, und
          Artikel, die dort liegen, bleiben dort, bis jemand sie umbucht.
        </p>
      )}
    </div>
  );
}
